import { eq } from 'tio-ui/utilities';
import { hash } from '@ember/helper';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';

import Component from '@glimmer/component';
import ForgivenessEstimatorForgivenessEstimatorForm from 'tio-employee/components/forgiveness-estimator/forgiveness-estimator-form';
import StrategyFinderStrategyFinderForm from 'tio-common/components/strategy-finder/strategy-finder-form';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type AccountModel from 'tio-data/models/account';
import type IntlService from 'ember-intl/services/intl';
import type PersonModel from 'tio-data/models/person';
import type RecommendationModel from 'tio-data/models/recommendation';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type UserModel from 'tio-data/models/user';
import { Section, VStack } from 'tio-ui/components/layout';
import { on } from '@ember/modifier';
import Button from 'tio-ui/components/button';
import type { ObservableSource } from 'tio-employee/services/observability';

export interface MonthlyPaymentForgivenessCardSignature {
  Args: {
    accounts: AccountModel[];
    loanSummary: LoanSummary;
    person: PersonModel;
    programType: string;
    user: UserModel;
    isCanceling: boolean;
  };
  Element: HTMLDivElement;
}

export default class MonthlyPaymentForgivenessCardComponent extends Component<MonthlyPaymentForgivenessCardSignature> {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;
  @service declare intl: IntlService;

  @tracked showEstimator = false;

  checkLoansAndGoToEstimator = () => {
    this.showEstimator = true;
  };

  didSaveEstimator = (recommendation: RecommendationModel) => {
    if (this.args.programType === 'idr') {
      this.router.transitionTo(
        'authenticated.idr.dashboard.forgiveness-estimator.results',
        recommendation
      );
    } else if (this.args.programType === 'sf') {
      this.router.transitionTo(
        'authenticated.repaying-student-debt.repayment-strategy-finder.dashboard.questionnaire.results',
        recommendation
      );
    }
  };

  get source(): ObservableSource {
    switch (this.args.programType) {
      case 'idr':
        return 'idr';
      case 'sf':
        return 'strategy_finder';
      default:
        return 'pslf';
    }
  }

  get shouldShowUploadPrompt() {
    if (!this.args.accounts.length || !this.args.loanSummary?.summaryByLoanType?.length) {
      return true;
    }
    const errors = this.args.loanSummary?.errors || [];
    const unknownLoanTypeError = errors.find((error) => error.message === 'ambiguous_loan_type');
    if (!unknownLoanTypeError) return false;
    return this.args.accounts.some(
      (acc) =>
        acc.id === unknownLoanTypeError.accountId &&
        acc.loans.some((loan) => loan.id === unknownLoanTypeError.loanId)
    );
  }

  <template>
    <Section>
      <:body>
        {{#if this.shouldShowUploadPrompt}}
          <VStack class="text-center items-center">
            <div class="text-left max-w-lg">
              <p class="mb-4">
                {{t "strategy_finder.missing_loan_types.message"}}
              </p>
              <p class="mb-6">
                {{t "strategy_finder.missing_loan_types.upload_prompt"}}
              </p>
            </div>
            <Button
              @intent="primary"
              {{on
                "click"
                (transitionTo
                  "authenticated.observability.upload"
                  (hash queryParams=(hash source=this.source providers="nslds"))
                )
              }}
            >
              {{t "strategy_finder.missing_loan_types.upload_button"}}
            </Button>
            {{svgJar
              "calculator-illustration"
              width="50%"
              height="50%"
              role="img"
              title=(t "svg.calculator_illustration")
            }}
          </VStack>
        {{else if this.showEstimator}}
          {{#if (eq @programType "idr")}}
            <h2 class="text-midnight font-semibold mb-2">
              {{t "idr.estimator.default"}}
            </h2>
            <p class="font-semibold md:w-7/12">
              {{t "idr.estimator.complete"}}
            </p>
            <ForgivenessEstimatorForgivenessEstimatorForm
              @application="IDR"
              @loanSummary={{@loanSummary}}
              @didSave={{this.didSaveEstimator}}
              class="md:max-w-3xl"
            />
          {{else}}
            <h2 class="text-midnight font-semibold mb-2">
              {{t "common.strategy_finder.default"}}
            </h2>
            <p class="font-semibold">
              {{t "strategy_finder.complete"}}
            </p>
            <StrategyFinderStrategyFinderForm
              @user={{@user}}
              @application="STRATEGY_FINDER"
              @loanSummary={{@loanSummary}}
              @didSave={{this.didSaveEstimator}}
              class="md:max-w-3xl ml-4"
            />
          {{/if}}
        {{else}}
          <VStack class="text-center items-center">
            <p class="text-left">
              {{#if (eq @programType "idr")}}
                {{t
                  "idr.estimator.idr_explanations"
                  htmlSafe=true
                  color="text-violet-800 font-semibold"
                }}
              {{else}}
                {{t
                  "strategy_finder.intro_explanation"
                  htmlSafe=true
                  color="text-violet-800 font-semibold"
                }}
              {{/if}}
            </p>
            <div>
              <Button @intent="primary" {{on "click" this.checkLoansAndGoToEstimator}}>
                {{t "idr.estimator.start"}}
              </Button>
            </div>
            {{svgJar
              "calculator-illustration"
              width="50%"
              height="50%"
              role="img"
              title=(t "svg.calculator_illustration")
            }}
          </VStack>
        {{/if}}
      </:body>
    </Section>
  </template>
}
